import { isFuture } from 'date-fns';
import { useMemo } from 'react';

import {
  Currency,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { Card } from '@sorare/core/src/lib/cards';

export const useSortByPrice = <T extends Card>() => {
  const { toMonetaryAmount, toMonetaryAmountInReferenceCurrency } =
    useMonetaryAmount();
  const { currency, fiatCurrency, cryptocurrency } = useCurrentUserCurrencies();

  return useMemo(() => {
    const sortCurrency =
      currency === Currency.ETH ? cryptocurrency : fiatCurrency;

    const getPriceRangeAverage = (
      priceRange: NonNullable<Card['priceRange']>
    ) => {
      const monetaryAmount = toMonetaryAmountInReferenceCurrency(
        (priceRange.max + priceRange.min) / 2n,
        SupportedCurrency.WEI
      );
      return monetaryAmount.readAmount(sortCurrency);
    };

    const getCardPrice = (card: T): bigint | number => {
      const {
        liveSingleSaleOffer,
        tokenOwner,
        livePrimaryOffer,
        latestEnglishAuction,
        priceRange,
      } = card;

      if (liveSingleSaleOffer) {
        const monetaryAmount = toMonetaryAmount(
          liveSingleSaleOffer.receiverSide.amounts
        );
        return monetaryAmount.readAmount(sortCurrency);
      }
      if (tokenOwner?.amounts?.wei && tokenOwner.amounts.wei !== 0n) {
        const monetaryAmount = toMonetaryAmount(tokenOwner.amounts);
        return monetaryAmount.readAmount(sortCurrency);
      }
      if (livePrimaryOffer?.price && isFuture(livePrimaryOffer.endDate)) {
        const monetaryAmount = toMonetaryAmount(livePrimaryOffer.price);
        return monetaryAmount.readAmount(sortCurrency);
      }
      if (latestEnglishAuction && isFuture(latestEnglishAuction.endDate)) {
        const { currency: latestEnglishAuctionCurrency, currentPrice } =
          latestEnglishAuction;
        const monetaryAmount = toMonetaryAmountInReferenceCurrency(
          currentPrice,
          latestEnglishAuctionCurrency
        );
        return monetaryAmount.readAmount(sortCurrency);
      }
      if (priceRange) {
        return getPriceRangeAverage(priceRange);
      }
      return 0n;
    };

    return (card: T[], sortOrder: 'asc' | 'desc') => {
      const res = [...card];
      res.sort((a, b) => Number(getCardPrice(b)) - Number(getCardPrice(a)));
      if (sortOrder === 'asc') res.reverse();
      return res;
    };
  }, [
    toMonetaryAmount,
    toMonetaryAmountInReferenceCurrency,
    currency,
    fiatCurrency,
    cryptocurrency,
  ]);
};
